/**
 * Capa de acceso a datos (Supabase) para Finanzas.
 * Reemplaza el uso directo de Firestore. Las columnas de Postgres están en camelCase
 * (calzan con types.ts); las colecciones camelCase de Firestore mapean a tablas snake_case.
 *
 * A diferencia de Tierra, los datos son PER-USER: casi toda operación va filtrada por
 * "userId" (el dueño efectivo; la delegación linkedTo la resuelven las RLS en Postgres).
 *
 * Equivalencias con Firestore:
 *   onSnapshot(collection(db,'users',uid,'x'), cb) → subscribeUser('x', uid, cb)
 *   addDoc(collection(...), data)                  → insertRow('x', data)  (data lleva userId)
 *   updateDoc(doc(...,id), data)                   → updateRow('x', id, data)
 *   deleteDoc(doc(...,id))                         → deleteRow('x', id)
 *   getDoc(doc(...,id))                            → getRow('x', id)
 *   writeBatch con balances                        → callRpc('create_transaction', ...) etc.
 */
import { getSupabase, getSchema } from "@potencial/supabase";
import { getAuthUser } from "./authState";

const TABLE: Record<string, string> = {
  accounts: "accounts",
  transactions: "transactions",
  plannedConcepts: "planned_concepts",
  plannedItems: "planned_items",
  plannedMatches: "planned_matches",
  planningCategories: "planning_categories",
  accountingAccounts: "accounting_accounts",
  accountingEntries: "accounting_entries",
  managedArticles: "managed_articles",
  managedDocuments: "managed_documents",
  managedContacts: "managed_contacts",
  moneyReceipts: "money_receipts",
  cashCounts: "cash_counts",
  settings: "settings",
  system_logs: "system_logs",
  profiles: "profiles",
  allowedEmails: "allowed_emails",
};
const tbl = (collection: string): string => TABLE[collection] ?? collection;

export interface QueryOpts {
  orderBy?: string;
  ascending?: boolean;
  limit?: number;
}

// PostgREST corta cada respuesta en PGRST_DB_MAX_ROWS (1000 en el VPS) sin dar error:
// una lectura sin paginar deja afuera filas en silencio cuando la tabla pasa ese tamaño.
const PAGE_SIZE = 1000;

/**
 * Filas del dueño paginando de a PAGE_SIZE. Orden estable con "id" como desempate
 * para que las páginas no se solapen ni salteen filas.
 */
async function selectUserRows<T>(table: string, userId: string, opts?: QueryOpts): Promise<T[]> {
  const rows: T[] = [];
  const wanted = opts?.limit ?? Infinity;
  for (let from = 0; rows.length < wanted; from += PAGE_SIZE) {
    const to = from + Math.min(PAGE_SIZE, wanted - rows.length) - 1;
    let q = getSupabase().from(table).select("*").eq("userId", userId);
    if (opts?.orderBy) q = q.order(opts.orderBy, { ascending: opts.ascending ?? true });
    q = q.order("id", { ascending: true }).range(from, to);
    const { data, error } = await q;
    if (error) throw error;
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < to - from + 1) break;
  }
  return rows;
}

/** Error handler compatible con el formato del handleFirestoreError original. */
export function handleDbError(error: unknown, operationType: string, path: string | null): never {
  const user = getAuthUser();
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path,
    authInfo: { userId: user?.uid, email: user?.email },
  };
  console.error("DB Error: ", JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

/** Id de cliente (equivalente a doc(collection(...)).id de Firestore). */
export const newId = (): string => crypto.randomUUID();

// Sufijo único por suscripción: dos componentes pueden suscribir la misma tabla+usuario
// a la vez (ej. planned_items desde dos vistas); si el nombre de canal se repite,
// supabase-js devuelve el canal YA suscripto y agregar callbacks tira
// "cannot add postgres_changes callbacks after subscribe()".
let channelSeq = 0;

/**
 * Fetch inicial + realtime del set de filas del dueño (equivalente a onSnapshot
 * sobre una subcolección de users/{userId}).
 */
export function subscribeUser<T>(
  collection: string,
  userId: string,
  onData: (rows: T[]) => void,
  opts?: QueryOpts,
  onError?: (err: unknown) => void,
): () => void {
  const sb = getSupabase();
  const table = tbl(collection);
  let active = true;

  const load = async () => {
    let rows: T[];
    try {
      rows = await selectUserRows<T>(table, userId, opts);
    } catch (error) {
      onError?.(error);
      return;
    }
    if (active) onData(rows);
  };
  void load();

  const channel = sb
    .channel(`rt:${table}:${userId}:${++channelSeq}`)
    .on(
      "postgres_changes",
      { event: "*", schema: getSchema(), table, filter: `userId=eq.${userId}` },
      () => void load(),
    )
    .subscribe();

  return () => {
    active = false;
    void sb.removeChannel(channel);
  };
}

/** Suscripción a una fila puntual por columna (ej. settings por userId). */
export function subscribeRow<T>(
  collection: string,
  matchCol: string,
  matchVal: string,
  onData: (row: T | null) => void,
  onError?: (err: unknown) => void,
): () => void {
  const sb = getSupabase();
  const table = tbl(collection);
  let active = true;

  const load = async () => {
    const { data, error } = await sb.from(table).select("*").eq(matchCol, matchVal).maybeSingle();
    if (error) {
      onError?.(error);
      return;
    }
    if (active) onData((data as T) ?? null);
  };
  void load();

  const channel = sb
    .channel(`rt:${table}:${matchCol}:${matchVal}:${++channelSeq}`)
    .on(
      "postgres_changes",
      { event: "*", schema: getSchema(), table, filter: `${matchCol}=eq.${matchVal}` },
      () => void load(),
    )
    .subscribe();

  return () => {
    active = false;
    void sb.removeChannel(channel);
  };
}

export async function listUserRows<T>(collection: string, userId: string, opts?: QueryOpts): Promise<T[]> {
  return selectUserRows<T>(tbl(collection), userId, opts);
}

export async function getRow<T>(collection: string, id: string, idCol = "id"): Promise<T | null> {
  const { data, error } = await getSupabase().from(tbl(collection)).select("*").eq(idCol, id).maybeSingle();
  if (error) throw error;
  return (data as T) ?? null;
}

export async function insertRow<T = { id: string }>(
  collection: string,
  values: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await getSupabase().from(tbl(collection)).insert(values).select().single();
  if (error) throw error;
  return data as T;
}

export async function updateRow(
  collection: string,
  id: string,
  values: Record<string, unknown>,
  idCol = "id",
): Promise<void> {
  const { error } = await getSupabase().from(tbl(collection)).update(values).eq(idCol, id);
  if (error) throw error;
}

export async function upsertRow<T = { id: string }>(
  collection: string,
  values: Record<string, unknown>,
  onConflict = "id",
): Promise<T> {
  const { data, error } = await getSupabase().from(tbl(collection)).upsert(values, { onConflict }).select().single();
  if (error) throw error;
  return data as T;
}

export async function deleteRow(collection: string, id: string, idCol = "id"): Promise<void> {
  const { error } = await getSupabase().from(tbl(collection)).delete().eq(idCol, id);
  if (error) throw error;
}

/** where('col','array-contains',val) de Firestore sobre columnas uuid[]/text[]. */
export async function listRowsArrayContains<T>(
  collection: string,
  userId: string,
  arrayCol: string,
  value: string,
): Promise<T[]> {
  const { data, error } = await getSupabase()
    .from(tbl(collection))
    .select("*")
    .eq("userId", userId)
    .contains(arrayCol, [value]);
  if (error) throw error;
  return (data ?? []) as T[];
}

export async function callRpc<T = unknown>(fn: string, params: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabase().rpc(fn, params);
  if (error) throw error;
  return data as T;
}
